import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { crmGetPipelineStages } from '../api';

export interface PipelineStage {
  id: number;
  code: string;
  label: string;
  order: number;
  probability: number;
  color: string;
  isActive: boolean;
  isTerminal: boolean;
  module: string;
  _count?: { gateRules: number };
}

interface PipelineStagesContextType {
  stages: PipelineStage[];
  stageMap: Record<string, PipelineStage>;
  loading: boolean;
  refresh: () => Promise<void>;
  getStageLabel: (code: string) => string;
  getStageColor: (code: string) => string;
  getStageOrder: (code: string) => number;
  getStageProbability: (code: string) => number;
  activeStages: PipelineStage[];
}

const PipelineStagesContext = createContext<PipelineStagesContextType>({
  stages: [],
  stageMap: {},
  loading: true,
  refresh: async () => {},
  getStageLabel: () => '',
  getStageColor: () => '#1890ff',
  getStageOrder: () => 0,
  getStageProbability: () => 0,
  activeStages: [],
});

export const usePipelineStages = () => useContext(PipelineStagesContext);

export const PipelineStagesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await crmGetPipelineStages();
      setStages(data);
    } catch (err) {
      console.error('Failed to load pipeline stages:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const stageMap: Record<string, PipelineStage> = {};
  for (const s of stages) {
    stageMap[s.code] = s;
  }

  const activeStages = stages.filter(s => s.isActive);

  const getStageLabel = (code: string) => stageMap[code]?.label || code;
  const getStageColor = (code: string) => stageMap[code]?.color || '#1890ff';
  const getStageOrder = (code: string) => stageMap[code]?.order || 0;
  const getStageProbability = (code: string) => stageMap[code]?.probability || 0;

  return (
    <PipelineStagesContext.Provider value={{
      stages, stageMap, loading, refresh,
      getStageLabel, getStageColor, getStageOrder, getStageProbability,
      activeStages,
    }}>
      {children}
    </PipelineStagesContext.Provider>
  );
};
